import { prepareImageForUpload } from '../../utils/imageResize';
import { getKindFromMimeType, KIND_IMAGE } from '../../components/patient-attachments/attachmentKinds';

/**
 * Preparo e envio de anexos do paciente, compartilhado pelo popup genérico de
 * upload e pelos satélites que enviam arquivos junto com o próprio registro
 * (ex.: as fotos do popup de medidas da assimetria craniana).
 *
 * Existe uma única implementação do envio: sequencial, um arquivo por
 * requisição, para não estourar o post_max_size e permitir mostrar progresso.
 */

/** Imagem redimensionada vira JPEG: o nome enviado precisa terminar em .jpg
 *  (o backend confere a extensão contra o conteúdo real do arquivo). */
export const toJpegName = (name) => {
    const base = (name || 'imagem').replace(/\.[^.]+$/, '');

    return `${base || 'imagem'}.jpg`;
};

/**
 * Prepara um arquivo escolhido: imagens são redimensionadas (full + miniatura)
 * e ganham preview local; os demais tipos seguem como estão.
 *
 * @param {File} file
 * @param {number} id identificador local do item
 * @returns {Promise<object>} item pronto para o envio
 */
export const prepareUploadItem = async (file, id) => {
    const kind = getKindFromMimeType(file.type);

    if (kind === KIND_IMAGE) {
        const prepared = await prepareImageForUpload(file);

        return {
            id: id,
            file: file,
            name: file.name,
            uploadName: toJpegName(file.name),
            kind: kind,
            blob: prepared.full,
            thumb: prepared.thumb,
            width: prepared.width,
            height: prepared.height,
            previewUrl: URL.createObjectURL(prepared.thumb),
            value: {}
        };
    }

    return {
        id: id,
        file: file,
        name: file.name,
        uploadName: file.name,
        kind: kind,
        blob: file,
        thumb: null,
        width: null,
        height: null,
        previewUrl: null,
        value: {}
    };
};

/** Libera o preview local do item. */
export const releaseUploadItem = (item) => {
    if (item && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
    }
};

/**
 * Envia um item. Nunca rejeita: devolve { ok, message }.
 */
export const uploadAttachmentItem = (upload, item, base, buildItemPayload) => {
    const extra = buildItemPayload ? (buildItemPayload(item.value, item) || {}) : {};

    const data = {
        ...base,
        file: item.blob,
        fileName: item.uploadName || item.name,
        thumb: item.thumb,
        width: item.width,
        height: item.height,
        ...extra
    };

    return new Promise((resolve) => {
        upload(data, (response) => {
            if (response && response.status === 'SUCCESS') {
                resolve({ ok: true });
            } else {
                resolve({ ok: false, message: (response && response.message) || `Não foi possível enviar "${item.name}".` });
            }
        }, (error) => {
            resolve({ ok: false, message: (error && error.message) || `Não foi possível enviar "${item.name}".` });
        });
    });
};

/**
 * Envia os itens em sequência.
 *
 * @param {function} upload UploadPatientAttachment do DataProvider
 * @param {Array} items itens de prepareUploadItem
 * @param {object} base campos comuns (patientId, appointmentId, context, contextId, documentDate)
 * @param {object} options { buildItemPayload, onProgress(atual, total) }
 * @returns {Promise<{success: number, failed: number, lastError: string, failedItems: Array}>}
 *          `failedItems` permite reenviar só o que falhou, sem duplicar o resto.
 */
export const uploadAttachmentItems = async (upload, items, base, options = {}) => {
    const { buildItemPayload, onProgress } = options;

    let success = 0;
    let lastError = '';
    const failedItems = [];

    for (let position = 0; position < items.length; position++) {
        if (onProgress) onProgress(position + 1, items.length);

        const result = await uploadAttachmentItem(upload, items[position], base, buildItemPayload);

        if (result.ok) {
            success++;
        } else {
            failedItems.push(items[position]);
            lastError = result.message;
        }
    }

    return { success: success, failed: failedItems.length, lastError: lastError, failedItems: failedItems };
};
